import React from 'react';
import toast from 'react-hot-toast';
import { isTestVersionActive } from '../services/redesignExperiment';

// Old-look ("control") pill styling — unchanged. In the new look the styling,
// icon theme and placement all come from the <AppToaster> defaults so toasts
// fired here look identical to direct react-hot-toast call sites.
const sharedStyle = {
  fontFamily: "Apercu",
  boxShadow: `
    0 20px 100px 20px rgba(113, 45, 33, 0.2),
    0 0 200px rgba(255, 255, 255, 0.9),
    0 0 60px rgba(255, 255, 255, 0.04)
  `,
  
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(2px)',
  borderRadius: '99px',
  border: '1px solid rgba(255, 255, 255, 0.8)',
};

const position = {
  position: 'bottom-center',
  style: {
    marginBottom: '1rem',
  },
};

// Stable id per type+message: re-dispatching the same message refreshes the
// existing toast (and restarts its timer) instead of stacking a duplicate.
const dedupeId = (type, message) =>
  typeof message === 'string' ? `${type}:${message}` : undefined;

const baseOptions = (type, message) => {
  if (isTestVersionActive()) {
    return { id: dedupeId(type, message) };
  }
  return {
    style: { ...sharedStyle, ...position.style },
    iconTheme: {
      secondary: '#FDF9F0',
    },
    position: position.position,
    id: dedupeId(type, message),
  };
};

// Consecutive load/connection failures across reloads. After more than 5 in a
// row we nudge the member toward support — sessionStorage so the streak
// survives "Tap to reload".
const LOAD_ERROR_STREAK_KEY = 'wg_load_error_streak';
const LOAD_ERROR_SUPPORT_AFTER = 5;
const SUPPORT_EMAIL = 'hey@wildgrain.com';

const getLoadErrorStreak = () => {
  try {
    const n = parseInt(sessionStorage.getItem(LOAD_ERROR_STREAK_KEY) || '0', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
};

const bumpLoadErrorStreak = () => {
  const next = getLoadErrorStreak() + 1;
  try {
    sessionStorage.setItem(LOAD_ERROR_STREAK_KEY, String(next));
  } catch {
    // Private mode / blocked storage — still show based on in-memory next.
  }
  return next;
};

const resetLoadErrorStreak = () => {
  try {
    sessionStorage.removeItem(LOAD_ERROR_STREAK_KEY);
  } catch {
    // ignore
  }
};

const reloadToastContent = (message, showSupport) => (t) => (
  <span className="inline-flex flex-wrap items-baseline gap-x-1 text-left">
    <button
      type="button"
      onClick={() => {
        toast.dismiss(t.id);
        window.location.reload();
      }}
      className="m-0 cursor-pointer border-0 bg-transparent p-0 text-left font-inherit text-inherit"
    >
      {message}{' '}
      <span className="underline underline-offset-2">Tap to reload</span>
    </button>
    {showSupport && (
      <span>
        · Still stuck?{' '}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="underline underline-offset-2"
          onClick={(e) => e.stopPropagation()}
        >
          {SUPPORT_EMAIL}
        </a>
      </span>
    )}
  </span>
);

const notification = {
  success: (message) => {
    resetLoadErrorStreak();
    return toast.success(message, baseOptions('success', message));
  },

  error: (message, options = {}) =>
    toast.error(message, {
      ...baseOptions('error', message),
      ...options,
    }),

  // Load/connection failures where reloading the app is the recovery path.
  // After more than 5 consecutive failures, also suggest emailing support.
  loadError: (message, options = {}) => {
    const streak = bumpLoadErrorStreak();
    const showSupport = streak > LOAD_ERROR_SUPPORT_AFTER;
    return toast.error(reloadToastContent(message, showSupport), {
      ...baseOptions('error', message),
      duration: showSupport ? 12000 : 8000,
      ...options,
    });
  },

  connectionError: (options = {}) =>
    notification.loadError('Connection issue!', options),

  loading: (message) =>
    toast.loading(
      message,
      isTestVersionActive()
        ? { id: dedupeId('loading', message) }
        : {
            style: { ...sharedStyle, ...position.style },
            position: position.position,
            id: dedupeId('loading', message),
          }
    ),

  // Clears the consecutive load-error streak after a successful data load.
  resetLoadErrors: () => {
    resetLoadErrorStreak();
  },

  // Pass-through so call sites can dismiss a loading toast without importing
  // react-hot-toast directly — notification is the app's single toast API.
  dismiss: (toastId) => toast.dismiss(toastId),

  promise: (promise, messages) =>
    toast.promise(
      promise,
      {
        loading: messages.loading || 'Loading...',
        success: messages.success || 'Success!',
        error: messages.error || 'Error occurred.',
      },
      isTestVersionActive()
        ? {}
        : {
            style: { ...sharedStyle, ...position.style },
            iconTheme: {
              secondary: '#FDF9F0',
            },
            position: position.position,
          }
    ),
};

export default notification;
